/**
 * Product-grid style class names, worn after the generic bk-* classes so the
 * pg value wins (last class wins). mjHead.ts defines them, the grid builder
 * wears them, and the Components step's selectors target them, so a rename
 * must update all three.
 *
 * All are mj-classes except `link`, a CSS class set by an mj-style block
 * (mj-text children can't take mj-classes).
 */
export const PG_CLASSES = {
  headline: "bk-pg-headline",
  name: "bk-pg-name",
  description: "bk-pg-description",
  price: "bk-pg-price",
  priceDropColor: "bk-pg-price-drop-color",
  quantity: "bk-pg-quantity",
  // Savings pill (price-drop percent off). The mj-text wears `savings` for
  // typography; the span wears `savingsPill`, whose background/border rules
  // live in ensurePriceDropPriceStyles.ts (mj-class can't reach a span).
  savings: "bk-pg-savings",
  savingsPill: "bk-pg-savings-pill",
  link: "bk-pg-link",
  // Name size rides these two builder-swapped classes, worn after bk-pg-name
  // so the size choice wins; dense vertical grids (3-4 columns) get compact.
  nameSize: "bk-pg-name-size",
  nameSizeCompact: "bk-pg-name-size-compact",
  card: "bk-pg-card",
  border: "bk-pg-border",
  image: "bk-pg-image",
  outlineColor: "bk-pg-outline-color",
  wrapper: "bk-pg-wrapper",
  wrapperCutout: "bk-pg-wrapper-cutout",
  // Review elements' own classes, worn last; derivation fills unset fields
  // (name/title/stars borrow the pg headline identity).
  reviewName: "bk-pg-review-name",
  reviewTitle: "bk-pg-review-title",
  reviewStar: "bk-pg-review-star",
  reviewBody: "bk-pg-review-body",
  reviewDate: "bk-pg-review-date",
  reviewVerified: "bk-pg-review-verified",
} as const;

/**
 * Product Grid Models
 *
 * These are the available item recommendation models that can be used
 * in product grid blocks. The `value` is the merge tag suffix used in
 * tile_data (e.g., tile_data.upsell_items).
 */
export const PRODUCT_GRID_MODELS = [
  { label: "Discover", value: "discover" },
  { label: "New Arrivals", value: "new_arrivals_items" },
  { label: "Cross Sell", value: "cross_sell_items" },
  { label: "Cart Items", value: "cart_items" },
  { label: "Trending", value: "trending_items" },
  { label: "Upsell", value: "upsell_items" },
  { label: "Compare", value: "compare_items" },
  { label: "Back In Stock", value: "back_in_stock_items" },
  { label: "Top Sellers", value: "top_sellers_items" },
  { label: "Low Stock", value: "low_stock_items" },
  { label: "Sale", value: "price_drop_items" },
  { label: "Magic", value: "magic_items" },
  { label: "Browse", value: "browsed_items" },
  { label: "Rebuy", value: "rebuy_items" },
] as const;

/**
 * Products carried on the triggering BYOC event (FOG-2602). Kept out of
 * PRODUCT_GRID_MODELS on purpose: that list feeds every model picker ungated,
 * and this source only exists inside a BYOC Dragon Tile.
 */
export const EVENT_ITEMS_MODEL = "event_items";
export const EVENT_ITEMS_LABEL = "Event Items";

export type ProductGridModelValue =
  (typeof PRODUCT_GRID_MODELS)[number]["value"];

/** Every `tile_data.<model>` an email product grid can bind to. */
export type ProductGridBoundModel =
  | ProductGridModelValue
  | typeof EVENT_ITEMS_MODEL;

export const DISCOVER_MODEL = "discover";

export const DISCOVER_SLOT_COUNT = 5;

/**
 * Backend `product_grids[].items` value for a model. The backend has no
 * "discover" category — its name for that grid is "dynamic".
 */
export function toBackendProductGridItems(model: string): string {
  return model === DISCOVER_MODEL ? "dynamic" : model;
}

/**
 * These are the properties available on each item within a product grid model.
 * Used as: tile_data.{model}.{index}.{property}
 * Example: tile_data.upsell_items.0.name
 */
export const ITEM_PROPERTIES = [
  "name",
  "item_url",
  "image_url",
  "price",
  "qty",
  "description",
  "return_url",
] as const;

export type ItemProperty = (typeof ITEM_PROPERTIES)[number];

export const TOGGLEABLE_ITEM_PROPERTIES = [
  { id: "name", label: "Name" },
  { id: "description", label: "Description" },
  { id: "image", label: "Image" },
  { id: "price", label: "Price" },
  { id: "savings", label: "Savings" },
  { id: "quantity", label: "Quantity" },
  { id: "badge", label: "Badge" },
  { id: "button", label: "Button" },
  { id: "divider", label: "Divider" },
] as const;

export type VisibleFieldItem = {
  id: string; // Property ID: "image", "name", "description", "price", "button"
  enabled: boolean; // Whether field is visible
};

export const DEFAULT_VISIBLE_FIELDS: VisibleFieldItem[] = [
  { id: "badge", enabled: false },
  { id: "image", enabled: true },
  { id: "name", enabled: true },
  { id: "description", enabled: false },
  { id: "price", enabled: true },
  { id: "savings", enabled: false },
  { id: "quantity", enabled: true },
  { id: "button", enabled: true },
];

export type ProductNameLinkMode = "no-link" | "linked";
export const PRODUCT_GRID_DEFAULT_NAME_LINK: ProductNameLinkMode = "no-link";

export const PRODUCT_GRID_NAME_TEXT_TRANSFORMS = [
  { id: "none", label: "Default text transform" },
  { id: "capfirst", label: "Capitalize first letter" },
  { id: "title", label: "Title case" },
  { id: "upper", label: "Uppercase" },
  { id: "lower", label: "Lowercase" },
] as const;

export type ProductNameTextTransform =
  (typeof PRODUCT_GRID_NAME_TEXT_TRANSFORMS)[number]["id"];
export const PRODUCT_GRID_DEFAULT_NAME_TEXT_TRANSFORM: ProductNameTextTransform =
  "none";

/** Editor preview placeholder for product name fields. */
export const PRODUCT_GRID_NAME_PREVIEW_TEXT = "Product Name";

export const PRODUCT_BUTTON_STYLES = [
  { id: "bk-button-primary", label: "Primary" },
  { id: "bk-button-secondary", label: "Secondary" },
  { id: "bk-button-tertiary", label: "Tertiary" },
] as const;

export type ProductButtonStyle = (typeof PRODUCT_BUTTON_STYLES)[number]["id"];
export const PRODUCT_GRID_DEFAULT_BUTTON_STYLE: ProductButtonStyle =
  "bk-button-secondary";
export const PRODUCT_GRID_BUTTON_DISPLAYS = [
  { id: "button", label: "Button" },
  { id: "link", label: "Text link" },
  // A linked graphic in place of the button (e.g. a brand's own styled CTA).
  { id: "image", label: "Image" },
] as const;
export type ButtonDisplayMode =
  (typeof PRODUCT_GRID_BUTTON_DISPLAYS)[number]["id"];
export const PRODUCT_GRID_DEFAULT_BUTTON_DISPLAY: ButtonDisplayMode = "button";
/** Widest an image CTA can be set to: the email body width. */
export const PRODUCT_GRID_BUTTON_IMAGE_MAX_WIDTH = 600;
export type ButtonWidthMode = "content" | "full";
export const PRODUCT_GRID_DEFAULT_BUTTON_WIDTH: ButtonWidthMode = "content";

export const PRICE_DISPLAY_OPTIONS = [
  { label: "Price before", value: "price_before" },
  {
    label: "Price before + Sale price",
    value: "price_before_and_sale_price",
  },
  // "price" is the current (sale) price field — also the default plain display.
  { label: "Sale price", value: "price" },
] as const;

export type PriceDisplayMode = (typeof PRICE_DISPLAY_OPTIONS)[number]["value"];

export const PRODUCT_GRID_DEFAULT_PRICE_DISPLAY: PriceDisplayMode = "price";

export const PRICE_DISPLAY_STORED_VALUES = PRICE_DISPLAY_OPTIONS.map(
  (option) => option.value,
);

/** Coerce a stored/unknown value to a valid display mode; defaults to "price". */
export function normalizePriceDisplayMode(
  value: string | undefined,
): PriceDisplayMode {
  if (value === "price_before" || value === "price_before_and_sale_price") {
    return value;
  }
  return "price";
}

export type PriceLayoutMode = "inline" | "stacked";
export const PRODUCT_GRID_DEFAULT_PRICE_LAYOUT: PriceLayoutMode = "inline";

export const PRICE_COLOR_OPTIONS = [
  // No price-drop color; both prices use the default text color.
  { label: "Default Color (All)", value: "default" },
  // Price-drop color on both prices, via the bk-price-drop-color mj-class.
  { label: "Sale Color (All)", value: "price_drop_all" },
  // Price-drop color on the sale price only (combined price display only).
  { label: "Sale Color (Sale Price)", value: "price_drop_sale_price" },
] as const;

export type PriceColorMode = (typeof PRICE_COLOR_OPTIONS)[number]["value"];

// "price_drop_all" matches the pre-FOG-1894 rendering, so existing grids are
// unchanged until someone picks a different option.
export const PRODUCT_GRID_DEFAULT_PRICE_COLOR: PriceColorMode =
  "price_drop_all";

/** Coerce a stored/unknown value to a valid price color; defaults to "price_drop_all". */
export function normalizePriceColorMode(
  value: string | undefined,
): PriceColorMode {
  if (value === "default" || value === "price_drop_sale_price") {
    return value;
  }
  return "price_drop_all";
}

/**
 * "Sale Color (Sale Price)" only differs from "(All)" when both prices
 * show; in any single-price display it means the same thing (color the one
 * visible price), so it resolves to "price_drop_all" there. Used by both the
 * trait dropdown and the grid builder so they always agree. The stored value
 * is left untouched, so switching back to the combined display restores the
 * sale-price choice.
 */
export function resolveEffectivePriceColor(
  priceColor: string | undefined,
  priceDisplay: string | undefined,
): PriceColorMode {
  const color = normalizePriceColorMode(priceColor);
  if (
    color === "price_drop_sale_price" &&
    normalizePriceDisplayMode(priceDisplay) !== "price_before_and_sale_price"
  ) {
    return "price_drop_all";
  }
  return color;
}

// Default text before each price in the combined price-drop display.
export const PRODUCT_GRID_DEFAULT_PRICE_WAS_TEXT = "Was";
export const PRODUCT_GRID_DEFAULT_PRICE_NOW_TEXT = "Now";

export const PRICE_SIZE_OPTIONS = [
  { label: "Default Size (All)", value: "default" },
  // Shrinks the was-price to font_size_xs.
  { label: "Small (Before Price)", value: "small_price_before" },
] as const;

export type PriceSizeMode = (typeof PRICE_SIZE_OPTIONS)[number]["value"];

export const PRODUCT_GRID_DEFAULT_PRICE_SIZE: PriceSizeMode = "default";

// Savings pill (price-drop grids only): "<before>NN<after>", e.g. "50% OFF".
export const PRODUCT_GRID_DEFAULT_SAVINGS_BEFORE_TEXT = "";
export const PRODUCT_GRID_DEFAULT_SAVINGS_AFTER_TEXT = "% OFF";
/** Canvas stand-in for the percent, which only Gonja can compute. */
export const PRODUCT_GRID_SAVINGS_PREVIEW_PERCENT = "XX";

export type ProductImageBorderMode = "no-border" | "bordered";
export const PRODUCT_GRID_DEFAULT_IMAGE_BORDER: ProductImageBorderMode =
  "no-border";

export const PRODUCT_GRID_DEFAULT_IMAGE_BORDER_RADIUS = 0;

export type BadgeTheme = "colorful" | "black" | "outlined";
export const PRODUCT_GRID_DEFAULT_BADGE_THEME: BadgeTheme = "colorful";

export type BadgeStyle = "floating" | "full-width";
export const PRODUCT_GRID_DEFAULT_BADGE_STYLE: BadgeStyle = "floating";

export interface BadgeConfig {
  color: string;
  textColor: string;
}

export const MODEL_TO_BADGE: Record<string, BadgeConfig> = {
  cart_items: {
    color: "#CEE7EC",
    textColor: "#000000",
  },
  cross_sell_items: {
    color: "#FFE032",
    textColor: "#000000",
  },
  upsell_items: {
    color: "#FFE032",
    textColor: "#000000",
  },
  compare_items: {
    color: "#FFE032",
    textColor: "#000000",
  },
  rebuy_items: {
    color: "#4F37EB",
    textColor: "#FFFFFF",
  },
  new_arrivals_items: {
    color: "#D2FF61",
    textColor: "#000000",
  },
  price_drop_items: {
    color: "#C3FFF6",
    textColor: "#000000",
  },
  back_in_stock_items: {
    color: "#B6FF91",
    textColor: "#000000",
  },
  low_stock_items: {
    color: "#FBFF24",
    textColor: "#000000",
  },
  trending_items: {
    color: "#892AB5",
    textColor: "#FFFFFF",
  },
  top_sellers_items: {
    color: "#97FFD4",
    textColor: "#000000",
  },
  browsed_items: {
    color: "#2A35FF",
    textColor: "#FFFFFF",
  },
  sale_velocity_items: {
    color: "#EB4337",
    textColor: "#FFFFFF",
  },
};

export const DEFAULT_BADGE_BG_COLOR = "#000000";
export const DEFAULT_BADGE_TEXT_COLOR = "#FFFFFF";

export const PRODUCT_GRID_COMPONENT_TYPE = "product-grid";
export const PRODUCT_GRID_CATEGORY = "Content";
export const PRODUCT_GRID_DEFAULT_MODEL = "cart_items";

export const PRODUCT_GRID_WRAPPER_DEFAULT_PADDING = "30px 20px 20px 20px";

export const PRODUCT_GRID_CART_ITEMS_CTA = "Return to cart";
export const PRODUCT_GRID_BROWSED_ITEMS_CTA = "View items";

/**
 * Parse CSS padding shorthand and return left + right total in px.
 * Supports 1–4 space-separated tokens (e.g. "10px", "0 10px", "0 10px 0 10px").
 */
export function sumHorizontalPaddingPx(padding: string): number {
  const tokens = padding
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((t) => {
      const m = /^(\d+(?:\.\d+)?)px$/i.exec(t);
      return m ? parseFloat(m[1]) : NaN;
    });
  if (tokens.length === 0 || tokens.some((n) => Number.isNaN(n))) return 0;
  if (tokens.length === 1) return 2 * tokens[0];
  if (tokens.length === 2) return 2 * tokens[1];
  if (tokens.length === 3) return 2 * tokens[1];
  return tokens[1] + tokens[3];
}

/**
 * Available layout options for product grids.
 * Format: "RxC" where R = rows, C = columns
 *
 * cardLayouts: Indicates which card layouts this grid layout supports:
 * - ["image-above"]: Multi-column layouts (products side-by-side)
 * - ["image-left", "image-right"]: Single-column horizontal layouts (image beside content)
 * - ["image-above", "image-left", "image-right"]: Universal (1x1 works with all)
 */
export const PRODUCT_GRID_LAYOUTS = [
  {
    id: "1x1",
    label: "1x1",
    rows: 1,
    cols: 1,
    cardLayouts: ["image-above", "image-left", "image-right"],
  },
  {
    id: "1x2",
    label: "1x2",
    rows: 1,
    cols: 2,
    cardLayouts: ["image-above"],
  },
  {
    id: "1x3",
    label: "1x3",
    rows: 1,
    cols: 3,
    cardLayouts: ["image-above"],
  },
  {
    id: "1x4",
    label: "1x4",
    rows: 1,
    cols: 4,
    cardLayouts: ["image-above"],
  },
  {
    id: "2x1",
    label: "2x1",
    rows: 2,
    cols: 1,
    cardLayouts: ["image-left", "image-right", "image-alternate"],
  },
  {
    id: "2x2",
    label: "2x2",
    rows: 2,
    cols: 2,
    cardLayouts: ["image-above"],
  },
  {
    id: "2x3",
    label: "2x3",
    rows: 2,
    cols: 3,
    cardLayouts: ["image-above"],
  },
  {
    id: "3x1",
    label: "3x1",
    rows: 3,
    cols: 1,
    cardLayouts: ["image-left", "image-right", "image-alternate"],
  },
  {
    id: "3x3",
    label: "3x3",
    rows: 3,
    cols: 3,
    cardLayouts: ["image-above"],
  },
  {
    id: "4x3",
    label: "4x3",
    rows: 4,
    cols: 3,
    cardLayouts: ["image-above"],
  },
  {
    id: "4x1",
    label: "4x1",
    rows: 4,
    cols: 1,
    cardLayouts: ["image-left", "image-right", "image-alternate"],
  },
  {
    id: "5x1",
    label: "5x1",
    rows: 5,
    cols: 1,
    cardLayouts: ["image-left", "image-right", "image-alternate"],
  },
  {
    id: "5x2",
    label: "5x2",
    rows: 5,
    cols: 2,
    cardLayouts: ["image-above"],
  },
  {
    id: "5x3",
    label: "5x3",
    rows: 5,
    cols: 3,
    cardLayouts: ["image-above"],
  },
  {
    id: "6x1",
    label: "6x1",
    rows: 6,
    cols: 1,
    cardLayouts: ["image-left", "image-right", "image-alternate"],
  },
] as const satisfies ReadonlyArray<{
  id: string;
  label: string;
  rows: number;
  cols: number;
  cardLayouts: ProductGridCardLayoutId[];
}>;

export type ProductGridLayoutId = (typeof PRODUCT_GRID_LAYOUTS)[number]["id"];
export const PRODUCT_GRID_DEFAULT_LAYOUT: ProductGridLayoutId = "1x2";

/**
 * Card layout options for product grids.
 * Determines whether product image appears above, to the left, to the right, or alternating.
 */
export const PRODUCT_GRID_CARD_LAYOUTS = [
  { id: "image-above", label: "Image above" },
  { id: "image-left", label: "Image left" },
  { id: "image-right", label: "Image right" },
  { id: "image-alternate", label: "Alternating" },
] as const;

export type ProductGridCardLayoutId =
  (typeof PRODUCT_GRID_CARD_LAYOUTS)[number]["id"];
export const PRODUCT_GRID_DEFAULT_CARD_LAYOUT: ProductGridCardLayoutId =
  "image-above";

/**
 * Card pattern options for product grids.
 * Controls visual styling: border, background, content-only, or flat.
 * mjClass is applied to the card container (mj-column or mj-section) for MJML output.
 */
export const PRODUCT_GRID_CARD_PATTERNS = [
  { id: "flat", label: "Flat", mjClass: "" },
  {
    id: "outlined",
    label: "Outlined card",
    mjClass: `bk-border ${PG_CLASSES.border}`,
  },
  { id: "full", label: "Full card", mjClass: `bk-product ${PG_CLASSES.card}` },
  {
    id: "content",
    label: "Content card",
    mjClass: `bk-product ${PG_CLASSES.card}`,
  },
  {
    id: "full-outlined",
    label: "Full outlined card",
    mjClass: `bk-border bk-product ${PG_CLASSES.border} ${PG_CLASSES.card}`,
  },
] as const;

export type ProductGridCardPatternId =
  (typeof PRODUCT_GRID_CARD_PATTERNS)[number]["id"];
export const PRODUCT_GRID_DEFAULT_CARD_PATTERN: ProductGridCardPatternId =
  "flat";

export function isHorizontalCardLayout(
  layout: ProductGridCardLayoutId,
): boolean {
  return (
    layout === "image-left" ||
    layout === "image-right" ||
    layout === "image-alternate"
  );
}

export function isVerticalCardLayout(layout: ProductGridCardLayoutId): boolean {
  return layout === "image-above";
}

export function getCardPatternMjClass(
  pattern: ProductGridCardPatternId,
): string {
  return (
    PRODUCT_GRID_CARD_PATTERNS.find((p) => p.id === pattern)?.mjClass ?? ""
  );
}

/**
 * Text alignment options for product cards.
 * Controls text alignment for name, description, button, and price.
 */
export const PRODUCT_GRID_TEXT_ALIGNMENTS = [
  { id: "left", label: "Left" },
  { id: "center", label: "Center" },
  { id: "right", label: "Right" },
] as const;

export type ProductGridTextAlignmentId =
  (typeof PRODUCT_GRID_TEXT_ALIGNMENTS)[number]["id"];
export const PRODUCT_GRID_DEFAULT_TEXT_ALIGNMENT: ProductGridTextAlignmentId =
  "center";

export const PRODUCT_GRID_DEFAULT_HORIZONTAL_DIVIDER = "true";

/**
 * Review layout style options for horizontal product grids.
 * Controls the visual arrangement of review elements (avatar, name, stars, etc.).
 */
export const REVIEW_LAYOUT_STYLES = [
  { id: "style-1", label: "Style 1" },
  { id: "style-2", label: "Style 2" },
  { id: "style-3", label: "Style 3" },
  { id: "style-4", label: "Style 4" },
  { id: "style-5", label: "Style 5" },
] as const;

export type ReviewLayoutStyleId = (typeof REVIEW_LAYOUT_STYLES)[number]["id"];
export const PRODUCT_GRID_DEFAULT_REVIEW_LAYOUT_STYLE: ReviewLayoutStyleId =
  "style-4";

/**
 * Maps product grid model values to their display headings.
 * Used by syncHeadings to populate tile.data.product_grid_headings
 */
export const MODEL_TO_HEADING: Record<string, string> = {
  discover: "Recommended for You",
  new_arrivals_items: "Just Arrived",
  cross_sell_items: "Frequently Bought Together",
  cart_items: "In Your Cart",
  trending_items: "What Everyone's Loving",
  upsell_items: "Goes Well With",
  compare_items: "Compare Your Options",
  back_in_stock_items: "Back in Stock",
  top_sellers_items: "Top Picks This Week",
  low_stock_items: "Limited Availability",
  price_drop_items: "Newly Marked Down",
  magic_items: "Magic Items",
  browsed_items: "Keep Exploring",
  rebuy_items: "Buy Again",
  event_items: "Featured Items",
};
